import { useMemo } from 'react';
import {
  prefixMatchRecentSearch,
  type RecentSearchItem,
} from '@/utils/recentSearchHistory';

type InlineState = {
  hasGhost: boolean;
  matched: RecentSearchItem | null;
  /** 叠在输入框内的灰色后缀（父级包装层需 relative） */
  ghostLayer: React.ReactNode;
  /** 输入框 onKeyDown：已处理返回 true */
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => boolean;
};

/**
 * 分析栏历史补全：输入框内灰色后缀，Tab 或光标在末尾时按 → 采纳。
 */
export function useRecentSearchInline(
  value: string,
  onAccept: (full: string, item: RecentSearchItem) => void,
): InlineState {
  const match = useMemo(() => prefixMatchRecentSearch(value), [value]);
  const hasGhost = Boolean(match?.suffix);

  const completion = useMemo(() => {
    if (!match) return '';
    const q = value.trim().toLowerCase();
    const code = (match.item.creditCode || '').trim();
    if (code && code.toLowerCase().startsWith(q) && !match.item.name.toLowerCase().startsWith(q)) {
      return code;
    }
    return match.item.name;
  }, [match, value]);

  const ghostLayer =
    hasGhost && match ? (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 flex items-center overflow-hidden text-sm"
      >
        <span className="invisible whitespace-pre">{value}</span>
        <span className="truncate whitespace-pre text-foreground-300">{match.suffix}</span>
      </div>
    ) : null;

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (!hasGhost || !match) return false;
    const el = e.currentTarget;
    const atEnd =
      el.selectionStart === value.length && el.selectionEnd === value.length;

    if (e.key === 'Tab' || (e.key === 'ArrowRight' && atEnd)) {
      e.preventDefault();
      onAccept(completion, match.item);
      return true;
    }
    return false;
  };

  return {
    hasGhost,
    matched: match?.item ?? null,
    ghostLayer,
    onInputKeyDown,
  };
}
